'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { Fragment, type MouseEvent, useMemo } from 'react';
import AppLink from '@/components/elementary/AppLink';
import { isDefaultLocale, sortLocaleLinksByAppLocales } from '@/components/elementary/LocaleSwitcher/utils';
import { Check } from '@/components/icons/Check';
import { Dropdown } from '@/components/icons/Dropdown';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { LOCALE_TRANSLATION } from '@/constants/i18n.constants';
import { cn } from '@/lib/styles';
import type { AppLocale } from '@/types/general';
import type { LocaleLink } from '@/types/i18n.types';
type Props = {
  currentLocale: AppLocale;
  localeLinks: LocaleLink[];
  onLocaleChange?: (locale: AppLocale) => void;
};
const LocaleSwitcher = t0 => {
  const $ = _c(25);
  const {
    currentLocale,
    localeLinks,
    onLocaleChange
  } = t0;
  const t = useTranslations("LocaleSwitcher");
  let t1;
  if ($[0] !== localeLinks) {
    t1 = sortLocaleLinksByAppLocales(localeLinks);
    $[0] = localeLinks;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sortedLinks = t1;
  let t2;
  if ($[2] !== onLocaleChange) {
    t2 = locale => event => {
      if (!onLocaleChange) {
        return;
      }
      event.preventDefault();
      onLocaleChange(locale);
    };
    $[2] = onLocaleChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleLocaleLinkClick = t2;
  let t3;
  if ($[4] !== t) {
    t3 = t("selectLanguage");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== currentLocale) {
    t4 = <span>{currentLocale}</span>;
    $[6] = currentLocale;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Dropdown className="size-5" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4) {
    t6 = <DropdownMenuTrigger asChild={true}><Button type="button" variant="none" size="none" aria-label={t3} className="h-9 w-16.5 justify-between rounded-sm border border-gray-400 bg-neutral-light-100 px-2 py-2.5 lok-text-body-bold-medium uppercase hover:border-neutral-500">{t4}{t5}</Button></DropdownMenuTrigger>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== currentLocale || $[13] !== handleLocaleLinkClick || $[14] !== sortedLinks) {
    let t8;
    if ($[16] !== currentLocale || $[17] !== handleLocaleLinkClick || $[18] !== sortedLinks.length) {
      t8 = (link, index) => {
        const isActive = link.locale === currentLocale;
        const useNativeAnchor = isDefaultLocale(link.locale);
        return <Fragment key={link.locale}><DropdownMenuItem asChild={true} className="p-0"><AppLink href={link.pathname} locale={link.locale} forceAnchor={useNativeAnchor} variant="none" size="none" lang={link.locale} aria-current={isActive ? "page" : undefined} className={cn("relative w-full rounded-sm px-2 py-1.5 pr-8", "hover:bg-accent focus:bg-accent focus:text-accent-foreground cursor-pointer", isActive ? "lok-text-body-bold-medium" : "lok-text-body-regular-medium")} onClick={handleLocaleLinkClick(link.locale)}><span>{LOCALE_TRANSLATION[link.locale as keyof typeof LOCALE_TRANSLATION]}</span>{isActive && <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center"><Check className="size-3" /></span>}</AppLink></DropdownMenuItem>{index < sortedLinks.length - 1 && <DropdownMenuSeparator />}</Fragment>;
      };
      $[16] = currentLocale;
      $[17] = handleLocaleLinkClick;
      $[18] = sortedLinks.length;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    t7 = sortedLinks.map(t8);
    $[12] = currentLocale;
    $[13] = handleLocaleLinkClick;
    $[14] = sortedLinks;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[20] !== t7) {
    t8 = <DropdownMenuContent className="hidden lg:block rounded-sm border-none shadow-box" side="bottom" align="end" sideOffset={18}>{t7}</DropdownMenuContent>;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t6 || $[23] !== t8) {
    t9 = <DropdownMenu>{t6}{t8}</DropdownMenu>;
    $[22] = t6;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
};
export default LocaleSwitcher;